import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getIdToken, clearTokens } from "../auth/lib/tokenStorage";
import { logOut } from "../auth/authSlice";
import { isTokenExpired, refreshCognitoTokens } from "../auth/lib/refreshTokens";
import { classifyError, ErrorType, getErrorMessage } from "../../utils/errorUtils";

const BASE_URL = process.env.REACT_APP_API_URL;

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers) => {
    const token = getIdToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
    return headers;
  },
});

const parseReadableStream = async (stream) => {
  if (!stream) return null;
  const reader = stream.getReader();
  let result = "";
  try {
    const { value } = await reader.read();
    const decoder = new TextDecoder("utf-8");
    const decoded = decoder.decode(value);
    result = JSON.parse(decoded);
  } catch (error) {
    console.error("Error parsing ReadableStream:", error);
  } finally {
    reader.releaseLock();
    return result;
  }
};

const isBadGroup = (args) => {
  let url = "";
  if (typeof args === "string") {
    url = args;
  } else if (args?.url) {
    url = args.url;
  }
  const splitByGroupsArray = url.split("/");
  return splitByGroupsArray.length === 3 && splitByGroupsArray[1] === "groups";
};

const hasInsufficientRights = (response) => {
  return (
    getErrorMessage(response?.error) ===
    "Method is not allowed for the user with current role/offer ownership status combo"
  );
};

const roleCheckingNotPerformed = (response) => {
  const message = getErrorMessage(response?.error);
  const result = message.includes("role checking was not performed");
  return result;
};

const forceLogout = (api) => {
  clearTokens();
  api.dispatch(logOut());
};

const attachRequestToError = async (result) => {
  if (result?.meta?.request) {
    const stream = result.meta.request.body;
    const body = await parseReadableStream(stream);
    result.error.request = {
      url: result.meta.request.url,
      body,
    };
  }
};

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const token = getIdToken();
  if (token && isTokenExpired(token)) {
    await refreshCognitoTokens();
  }

  let result = await baseQuery(args, api, extraOptions);

  const { type } = classifyError(result?.error);

  if (type === ErrorType.AUTH) {
    const refreshed = await refreshCognitoTokens();
    if (refreshed) {
      result = await baseQuery(args, api, extraOptions);
      const retry = classifyError(result?.error);
      if (retry.type === ErrorType.AUTH) forceLogout(api);
    } else {
      forceLogout(api);
    }
  } else if (type === ErrorType.FORBIDDEN) {
    if (
      !(
        isBadGroup(args) ||
        hasInsufficientRights(result) ||
        roleCheckingNotPerformed(result)
      )
    ) {
      forceLogout(api);
    }
  }

  if (result?.error) await attachRequestToError(result);
  return result;
};

export const baseUrl = BASE_URL;

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  reducerPath: "apiAuthenticated",
  endpoints: (builder) => ({}),
});
